import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import Button, { ButtonTypes } from '../../../UI/Button/Button';
import { CustomSelect } from '../../../UI/CustomSelect/CustomSelect';
import { Input } from '../../../UI/Input/Input';
import { Textarea } from '../../../UI/Textarea/Textarea';
import { CloseIcon } from '../../../assets/icons';
import { axiosInstance } from '../../../helpers/axios';
import { useAppSelector } from '../../../hooks/reduxHooks/useAppSelector';
import { PATH_DASHBOARD } from '../../../routes/RouterConfig';
import styles from './Vacancy.module.scss';
import { createUpdateValidationSchema } from './validationSchema';

type InitialValuesType = {
    position: string;
    required_experience: string;
    employment_form: { value: string; label: string }[];
    salaryFrom: string;
    salaryTo: string;
    description: string;
    skill: string;
};
export const CompanyVacancy = () => {
    const { id } = useAppSelector((state) => state.authReducer);
    const [skills, setSkilss] = useState<{ id: string; name: string }[]>([]);

    const navigate = useNavigate();

    const initialValues: InitialValuesType = {
        position: '',
        required_experience: '',
        employment_form: [],
        salaryFrom: '',
        salaryTo: '',
        description: '',
        skill: '',
    };

    const formik = useFormik({
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        initialValues,
        onSubmit: async () => {},
        validationSchema: createUpdateValidationSchema,
    });

    const onSubmit = async (status: 'OP' | 'CL') => {
        try {
            const url = `/v1/vacancy/`;
            const vacancyData = {
                status: status,
                position: formik.values.position,
                salary_from: formik.values.salaryFrom,
                salary_to: formik.values.salaryTo,
                required_experience: formik.values.required_experience,
                description: formik.values.description,
                skills: skills.length
                    ? skills.map((item) => item.name).join(', ')
                    : '',
                employment_form: formik.values.employment_form.map(
                    (item) => item.value,
                ),
                company: id,
            };
            await axiosInstance.post(url, vacancyData);
            toast.success('Данные успешно изменены');
            navigate(PATH_DASHBOARD.company.vacancies.root);
        } catch (e: unknown) {
            if (e instanceof AxiosError && e.response?.data.subscription) {
                toast.error(
                    <a href="mailto:career.law.msu@gmail.com">
                        Оплатите подписку для публикации новых вакансий:
                        career.law.msu@gmail.com»
                    </a>,
                );
            } else {
                toast.error('Произошла ошибка');
            }
        }
    };
    const required_experience: { value: string; label: string }[] = [
        { value: 'NO', label: 'Нет опыта' },
        { value: 'L1', label: 'До 1 года' },
        { value: '13', label: 'От 1 до 3 лет' },
        { value: 'M3', label: 'Более 3 лет' },
    ];

    const employment_form: { value: string; label: string }[] = [
        { value: 'FE', label: 'Полная занятость' },
        { value: 'PE', label: 'Частичная занятость' },
        { value: 'RW', label: 'Удаленная работа' },
        { value: 'IS', label: 'Стажировка' },
        { value: 'SH', label: 'Вахта' },
    ];

    return (
        <form className={styles.companyVacancy}>
            <div className={styles.companyVacancyTitle}>
                <div>
                    <h1>Вакансия</h1>
                    <span>
                        {new Date().toLocaleDateString('ru-Ru', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                        })}
                    </span>
                </div>

                <div>
                    <Button
                        onClick={() => onSubmit('OP')}
                        isDisabled={
                            !formik.isValid ||
                            !formik.dirty ||
                            skills.length === 0
                        }
                        className={classNames(
                            styles.companyVacancyButton_save,
                            formik.isValid &&
                                formik.dirty &&
                                skills.length !== 0 &&
                                styles.companyVacancyButton_valid,
                        )}
                    >
                        Опубликовать
                    </Button>
                    <Button
                        onClick={() => onSubmit('CL')}
                        isDisabled={
                            !formik.isValid ||
                            !formik.dirty ||
                            skills.length === 0
                        }
                        className={classNames(
                            styles.companyVacancyButton_save,
                            formik.isValid &&
                                formik.dirty &&
                                skills.length !== 0 &&
                                styles.companyVacancyButton_valid,
                        )}
                    >
                        Сохранить
                    </Button>
                </div>
            </div>
            <div className={styles.companyVacancyGrid}>
                <div>
                    <span>Должность</span>
                    <Input
                        wrapperClassName={[styles.EditAbout__input]}
                        headerClassName={styles.input__header}
                        fieldClassName={styles.input__field}
                        placeholder={'Должность'}
                        name="position"
                        value={formik.values.position}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.position}
                        isValid={
                            Boolean(formik.values.position) &&
                            !formik.errors.position
                        }
                    />
                </div>
                <div>
                    <span>Требуемый опыт</span>
                    <CustomSelect
                        onChange={(e: { value: string; label: string }) =>
                            formik.setFieldValue('required_experience', e.value)
                        }
                        borderRadius={'17px'}
                        fieldHeight={'55px'}
                        placeholderSize={'15px'}
                        placeholder={'Выберите опыт'}
                        options={required_experience}
                        isValid={
                            Boolean(formik.values.required_experience) &&
                            !formik.errors.required_experience
                        }
                    />
                </div>
                <div className={styles.companyVacancyGridSalary}>
                    <span>Заработная плата</span>
                    <Input
                        wrapperClassName={[styles.EditAbout__input]}
                        headerClassName={styles.input__header}
                        fieldClassName={styles.input__field}
                        placeholder={'От 30000'}
                        name="salaryFrom"
                        value={formik.values.salaryFrom}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.salaryFrom}
                        isValid={
                            Boolean(formik.values.salaryFrom) &&
                            !formik.errors.salaryFrom
                        }
                    />
                    <Input
                        wrapperClassName={[styles.EditAbout__input]}
                        headerClassName={styles.input__header}
                        fieldClassName={styles.input__field}
                        placeholder={'До 50000'}
                        name="salaryTo"
                        value={formik.values.salaryTo}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.salaryTo}
                        isValid={
                            Boolean(formik.values.salaryTo) &&
                            !formik.errors.salaryTo
                        }
                    />
                </div>
                <div>
                    <span>Занятость</span>
                    <CustomSelect
                        onChange={(e: { value: string; label: string }[]) => {
                            formik.setFieldValue('employment_form', e);
                        }}
                        borderRadius={'17px'}
                        fieldHeight={'55px'}
                        placeholderSize={'15px'}
                        placeholder={'Выберите занятость'}
                        options={employment_form}
                        isValid={
                            Boolean(formik.values.employment_form) &&
                            !formik.errors.employment_form
                        }
                        isMulti
                    />
                </div>
                <div className={styles.companyVacancyGridModifiedField}>
                    <span>Ключевые навыки</span>
                    <div>
                        <Input
                            wrapperClassName={[styles.EditAbout__input]}
                            headerClassName={styles.input__header}
                            fieldClassName={styles.input__field}
                            placeholder={'Добавьте навык'}
                            name="skill"
                            value={formik.values.skill}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isValid={!!skills.length}
                        />
                        <Button
                            type={ButtonTypes.AddButton}
                            isDisabled={formik.values.skill === ''}
                            onClick={() => {
                                if (formik.values.skill !== '') {
                                    setSkilss([
                                        ...skills,
                                        {
                                            id: uuidv4(),
                                            name: formik.values.skill,
                                        },
                                    ]);
                                }
                                formik.setFieldValue('skill', '');
                            }}
                        >
                            Добавить
                        </Button>
                        <div className={styles.companyVacancyExperience}>
                            {skills.map((item) => (
                                <div key={`${item.id}`}>
                                    <span>{item.name}</span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setSkilss([
                                                ...skills.filter(
                                                    (it) => it.id !== item.id,
                                                ),
                                            ])
                                        }
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className={styles.companyVacancyGridModifiedField}>
                    <span>Описание вакансии</span>
                    <Textarea
                        headerClassName={styles.input__header}
                        placeholder={'Описание вакансии'}
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={formik.errors.description}
                        isValid={
                            Boolean(formik.values.description) &&
                            !formik.errors.description
                        }
                    />
                </div>
            </div>
        </form>
    );
};
